/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import Avatar from '@ion/dls/src/components/avatar/Avatar';
import {CTALink} from '@ion/dls/src/components/buttons/CTALink';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import ClickAwayListener from '@ion/dls/src/components/listeners/ClickAwayListener';
import {ButtonSpinner} from '@ion/dls/src/components/loading/ButtonSpinner';
import Switch from '@ion/dls/src/components/switch/Switch';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import useToggleToolTip from '@ion/dls/src/hooks/useToggleToolTip';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import Render from '@ion/dls/src/render/Render';
import {shouldIncludeCalAmpReference, showHelpSection} from '@ion/dls/src/theme/resellers/Resellers';
import {dlsAlt, dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {HeaderTypography} from '@ion/dls/src/typography/HeaderTypography';
import {hasAppGroupChooserAccess, hasAppHelpAccess} from '@/security/Features';
import {editAdminUser} from '@/store/actions/admin-users';
import {showChangeGroup} from '@/store/actions/global-group-chooser';
import {getIONPrimeURL} from '@/store/helpers';
import {
  globalGroupChooserSelector,
  globalGroupTitleSelector
} from '@/store/selectors/global-group-chooser-selectors';
import {
  hasPreviewFeatureSelector,
  isAuthenticatingSelector,
  usernameSelector,
  userSelector
} from '@/store/selectors/login-selectors';
import {viewHelp, viewPrivacyPolicy} from '../../header/DesktopUserProfileView';
import {useLogout} from '../../hooks/useLogout';
import {isFromPrimeSelector} from '../../store/selectors/initialization-selectors';
import GlobalGroupChooserMobile from './GlobalGroupChooserMobile';

export interface UserProfileMobileProps {
  showProfile: boolean;
  handleClose: () => void;
  onContactSupportOpen: () => void;
}

const useStyles = makeStyles(() => ({
  mainContainer : {
    margin       : '40px',
    marginBottom : 0
  },
  subContainer : {
    display      : 'flex',
    width        : '100%',
    borderBottom : `1px solid ${dlsColor('grey4')}`
  },
  user : {
    display    : 'block',
    fontSize   : '16px',
    lineHeight : '20px',
    color      : dlsColor('grey1'),
    fontWeight : 500
  },
  account : {
    lineHeight : '20px',
    fontSize   : '14px',
    color      : dlsColor('grey3'),
    fontWeight : 400
  },
  ionLink : {
    ...dlsAlt('components', 'ionLinkStyle', 'ionLink')
  },
  disabled : {
    ...dlsAlt('components', 'ionLinkStyle', 'ionLink'),
    color : dlsColor('grey4')
  },
  betaLink : {
    backgroundColor : dlsColor('caBlue'),
    borderRadius    : '20px',
    marginLeft      : '25px',
    ...dlsAlt('components', 'ionpreviewText', 'new')
  },
  ionBeta : {
    display    : 'flex',
    alignItems : 'center'
  },
  customTooltip : {
    marginRight : '205px'
  },
  supLink : {
    position : 'relative',
    top      : '0.5em',
    left     : '-0.2em'
  },
  optionWrapper : {
    width          : '100%',
    alignItems     : 'center',
    display        : 'flex',
    justifyContent : 'space-between'
  },
  profileItem : {
    width          : '100%',
    alignItems     : 'center',
    display        : 'flex',
    justifyContent : 'space-between',
    marginTop      : '27px'
  },
  ctaLink : {
    display    : 'flex',
    alignItems : 'center'
  },
  ctaBold : {
    color  : dlsColor('grey2'),
    cursor : 'pointer',
    ...dlsTypography('cta')
  },
  container : {
    display      : 'flex',
    width        : '100%',
    padding      : 8,
    borderBottom : `1px solid ${dlsColor('grey5')}`
  },
  profileTitle : {
    width     : '100%',
    textAlign : 'center',
    marginTop : 6
  },
  userProfileHeader : {
    display       : 'flex',
    textTransform : 'uppercase',
    fontSize      : '20px',
    weight        : 500,
    lineHeight    : '30px',
    width         : '100%',
    alignItems    : 'center',
    verticalAlign : 'middle'
  },
  comingSoon : {
    display    : 'flex',
    alignItems : 'center',
    marginTop  : '27px'
  }
}));

/**
 * @userProfileMobile
 */
export const UserProfileMobile = ({
  showProfile,
  handleClose,
  onContactSupportOpen
}: UserProfileMobileProps) => {
  const canAccessHelp         = hasAppHelpAccess();
  const canAccessGroupChooser = hasAppGroupChooserAccess();
  const classes                         = useStyles();
  const dispatch                        = useDispatch();
  const accountName                     = useSelector(globalGroupTitleSelector);
  const {isViewing: displayChangeGroup} = useSelector(globalGroupChooserSelector);
  const isFromPrime                     = useSelector(isFromPrimeSelector);
  const username                        = useSelector(usernameSelector);
  const isLoggingOut                    = useSelector(isAuthenticatingSelector);
  const hasPreviewFeature               = useSelector(hasPreviewFeatureSelector);
  const currentUser                     = useSelector(userSelector);
  const [toggle, setToggle]             = useState(false);
  const content                         = translate('nav:ionprime.toolTipContent');
  const trademark                       = translate('nav:ionprime.trademark');
  const message                         = translate('nav:ionprime.toolTipSubContent');
  const [tooltipId, toggleToolTip]      = useToggleToolTip(null);
  const onLogout                        = useLogout();

  const onViewIONPrime = function() {
    setToggle(!toggle);
    window.open(getIONPrimeURL(window.location), '_self');
    if (!isFromPrime) {
      window.close();
    }
  };

  const handleTooltipClose = useCallback(function() {
    toggleToolTip('null');
  }, []);

  function ToggleTooltip(props: any) {
    return (
      <ClickAwayListener mountListener
        onClickAway={handleTooltipClose}>
        <Tooltip arrow
          open={tooltipId === props?.id}
          {...props} />
      </ClickAwayListener>
    );
  }

  const editProfile = function() {
    dispatch(editAdminUser(currentUser, true));
  };

  const toggleChangeGroup = function() {
    dispatch(showChangeGroup(!displayChangeGroup));
  };

  function onClose() {
    handleClose?.();
  }

  return (
    <Dialog fullScreen open={showProfile}>
      <div style={{width: '100%', height: '100%', overflow: 'hidden'}}>
        <div className={classes.container}>
          <div className={classes.userProfileHeader}>
            <IconButton icon={<MaterialExitCloseIcon />}
              style={{position: 'absolute'}}
              onClick={onClose}
              disableRipple />
            <HeaderTypography variant='h2' className={classes.profileTitle}>
              {translate('nav:ionprime.account')}
            </HeaderTypography>
          </div>
        </div>
        <div style={{height: '70%'}}>
          <div className={classes.mainContainer}>
            <div className={classes.subContainer}>
              <div>
                <Avatar name={username} />
              </div>
              <div style={{height: '64px', marginLeft: '24px'}}>
                <label className={classes.user}>{username}</label>
                <label className={classes.account}>{accountName}</label>
              </div>
            </div>
            <div>
              <Render.If condition={canAccessGroupChooser}>
                <div className={classes.profileItem}>
                  <CTALink className={classes.ctaLink} onClick={toggleChangeGroup}>
                    {translate('common:editors.shared.changeGroup')}
                  </CTALink>
                  <GlobalGroupChooserMobile toggleChangeGroup={toggleChangeGroup} />
                </div>
              </Render.If>
              <div className={classes.profileItem}>
                <CTALink className={classes.ctaLink} onClick={editProfile}>
                  {translate('nav:ionprime.profile')}
                </CTALink>
              </div>
              <Render.If condition={showHelpSection() && canAccessHelp}>
                <div className={classes.profileItem}>
                  <CTALink className={classnames(classes.ctaLink)}
                    onClick={viewHelp(onClose)}>
                    {translate('nav:ionprime.help')}
                  </CTALink>
                </div>
              </Render.If>
              <div className={classes.profileItem}>
                <CTALink className={classnames(classes.ctaLink)}
                  onClick={onContactSupportOpen}>
                  {translate('nav:ionprime.contactSupport')}
                </CTALink>
              </div>
              <Render.If condition={shouldIncludeCalAmpReference()}>
                <div className={classes.profileItem}>
                  <CTALink className={classnames(classes.ctaLink)}
                    onClick={viewPrivacyPolicy(onClose)}>
                    {translate('nav:ionprime.privacyPolicy')}
                  </CTALink>
                </div>
              </Render.If>
            </div>
          </div>
          {hasPreviewFeature &&
        <div className={classes.ionBeta}>
          <div style={{display: 'flex', width: '100%', justifyContent: 'space-between'}}>
            <div style={{whiteSpace: 'nowrap'}}>
              <ToggleTooltip id='toggle'
                title={
                  (<div>{content}<sup style={{textTransform: 'uppercase', fontSize: 10}}>{trademark}</sup>&ensp;{message}</div>)
                }>
                <div style={{paddingTop: '15px'}} className={classes.supLink} onClick={function(e) { toggleToolTip('toggle'); }}>
                  <sup className={classes.betaLink}>{translate('nav:ionprime.title')}</sup>
                </div>
              </ToggleTooltip>
              <CTALink style={{marginTop: 0, marginLeft: '40px'}} className={classes.ionLink}>{translate('nav:ionprime.preview')}</CTALink>
            </div>
            <div style={{display: 'flex', alignItems: 'center', marginTop: '22px'}}>
              <Switch onChange={onViewIONPrime} checked />
            </div>
          </div>
        </div>}
        </div>
        <div style={{height: '30%', marginLeft: '40px'}}>
          <Render>
            <Render.If condition={!isLoggingOut}>
              <CTALink style={{pointerEvents: isLoggingOut ? 'none' : 'initial'}}
                className={classes.ionLink}
                onClick={onLogout} >
                {translate('nav:ionprime.logout')}
              </CTALink>
            </Render.If>
            <Render.Else>
              <ButtonSpinner />
            </Render.Else>
          </Render>
        </div>
      </div>
    </Dialog>
  );
};
export default UserProfileMobile;
